<template>
  <!-- Sticky contents list for an article, in the column beside the prose.

       Worth the markup for two reasons: it is how a reader decides whether a
       long piece answers their question, and the anchors are what Google needs
       in order to offer "jump to" links for a section directly in a search
       result. -->
  <nav v-if="headings.length" :aria-label="label" class="text-sm">
    <p class="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">On this page</p>
    <ol class="space-y-2 border-l border-gray-800">
      <li
        v-for="heading in headings"
        :key="heading.id"
        :class="heading.level === 3 ? 'pl-6' : 'pl-4'"
      >
        <a
          :href="`#${heading.id}`"
          class="-ml-px block border-l-2 py-0.5 leading-6 transition"
          :class="active === heading.id
            ? 'border-indigo-400 pl-3 font-medium text-white'
            : 'border-transparent pl-3 text-gray-400 hover:border-gray-600 hover:text-gray-200'"
          @click="onSelect(heading)"
        >{{ heading.text }}</a>
      </li>
    </ol>
  </nav>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { trackEvent } from '~/composables/analytics'

const props = defineProps({
  // Post.headings from server/utils/blog.ts — h2 and h3 only.
  headings: { type: Array, default: () => [] },
  label: { type: String, default: 'Table of contents' },
})

// Which section the reader is in. Null until the observer reports one, so the
// prerendered HTML and the first client render agree (highlighting a guess on
// the server would be a hydration mismatch).
const active = ref(null)

let observer = null

onMounted(() => {
  if (!props.headings.length || typeof IntersectionObserver === 'undefined') return

  const targets = props.headings
    .map((heading) => document.getElementById(heading.id))
    .filter(Boolean)
  if (!targets.length) return

  // The rootMargin pins the trigger line just under the sticky header and
  // ignores the bottom 65% of the viewport, so the highlighted entry is the
  // heading a reader is actually under — not whichever one happens to be
  // visible at the foot of a tall screen.
  observer = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting)
      if (!visible.length) return
      // Topmost wins when a short section puts two headings in the band.
      const top = visible.reduce((best, entry) =>
        entry.boundingClientRect.top < best.boundingClientRect.top ? entry : best)
      active.value = top.target.id
    },
    { rootMargin: '-88px 0px -65% 0px', threshold: 0 },
  )

  for (const target of targets) observer.observe(target)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})

// Whether the contents list is used at all is the question that decides if it
// earns its place on the page; the section name says which parts people skip to.
const onSelect = (heading) => {
  try {
    trackEvent('Blog Contents Clicked', { section: heading.id })
  } catch {}
}
</script>
